import { useClarificationRequestModel } from "entities/clarification-requests/model/useClarificationRequestModel"
import { clarificationRequestResourceTypeMap } from "entities/clarification-requests/types/ClarificationRequest"
import {
  AggregatedProjectDataOptions,
  useAggregatedProjectData
} from "entities/projects/api/useAggregatedProjectData"
import { readonlyProjectPermissions } from "entities/projects/types/ProjectPermissions"

import { ProjectModel } from "../types/ProjectModel"
import { useProjectScopedActionHandlers } from "./useProjectScopedActionHandler"

export function useProject(
  { projectID }: { projectID: string },
  options?: AggregatedProjectDataOptions
): ProjectModel {
  const data = useAggregatedProjectData({
    projectID,
    options
  })
  const permissions = data.permissions?.data
  const isTeamProject = !!data.team?.data
  const role = data.role?.data

  const h = useProjectScopedActionHandlers({
    projectID,
    permissions: permissions ?? readonlyProjectPermissions,
    isTeamProject,
    role
  })

  const clarificationRequest = useClarificationRequestModel({
    resourceType: clarificationRequestResourceTypeMap.Project,
    id: projectID,
    projectID,
    options: { skip: !data.project?.data?.hasClarificationRequest }
  })

  return {
    core: {
      data: {
        project: data.project,
        permissions: data.permissions,
        role: data.role,
        phases: data.phases,
        meta: data.meta
      },
      handlers: {
        onOpen: h.onOpen,
        onShowDetails: h.onShowDetails,
        onEdit: h.onEdit,
        onAddClarificationRequest: h.onAddClarificationRequest,
        onDelete: h.onDelete,
        onActivate: h.onActivate,
        onDuplicate: h.onDuplicate,
        onShare: h.onShare,
        onExport: h.onExport,
        onGaebExport: h.onGaebExport,
        onMusterheftExport: h.onMusterheftExport,
        onOpenProjectHistory: h.onOpenProjectHistory
      }
    },
    team: {
      data: {
        team: data.team
      },
      handlers: {
        onManageTeam: h.onManageTeam,
        onInviteMember: h.onInviteMember,
        onLeaveTeam: h.onLeaveTeam
      }
    },
    pricing: {
      data: {
        price: data.price,
        priceLists: data.priceLists,
        standardInflationSurcharge: data.standardInflationSurcharge
      }
    },
    attachments: {
      data: {
        attachments: data.attachments,
        attachmentCategories: data.attachmentCategories
      },
      handlers: {
        onOpenAttachments: h.onOpenAttachments
      }
    },
    creation: {
      handlers: {
        onNewProduct: h.onNewProduct,
        onNewRoom: h.onNewRoom,
        onNewFloor: h.onNewFloor
      }
    },
    clarificationRequest
  }
}
